import { useUser } from "@clerk/tanstack-react-start";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowRight, Compass, Flower, RefreshCw } from "lucide-react";

import { SummitPromoCard } from "~/components/summit/summit-promo-card";
import { buttonVariants } from "~/components/ui/button";
import { PageTitle } from "~/components/ui/page-title";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/cn";
import {
  getCurrentFocusDisplay,
  getRecommendedCTA,
  shouldShowLBD,
} from "~/lib/intake/dashboard-display";
import { useUserEngagements } from "~/lib/queries/engagementQuery";
import { useUserIntakeResponse } from "~/lib/queries/intakeQuery";
import { getHourInClientTimezone } from "~/lib/utils/timezone";

import { FeaturedCoaches } from "./-components/featured-coaches";
import { UserCoaches } from "./-components/user-coaches";
import { UserConversations } from "./-components/user-conversations";
import { UserTasks } from "./-components/user-tasks";

export const Route = createFileRoute("/_user/dashboard/")({
  staticData: { title: "My Path" },
  component: RouteComponent,
});

function RouteComponent() {
  const getGreeting = () => {
    const hour = getHourInClientTimezone();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  const { user, isLoaded } = useUser();
  const { data: intakeResponse, isFetched } = useUserIntakeResponse();
  const { data: engagementData } = useUserEngagements(1, 1);

  const hasEngagements = (engagementData?.pagination.total ?? 0) > 0;
  const hasIntake = isFetched && intakeResponse != null;
  const noIntake = isFetched && intakeResponse == null;

  const focus = intakeResponse
    ? getCurrentFocusDisplay(intakeResponse.answers)
    : null;

  return (
    <>
      <div className="pb-3">
        {!isLoaded ? (
          <div className="flex items-center gap-2">
            <PageTitle>{getGreeting()},</PageTitle>
            <Skeleton className="h-8 w-32" />
          </div>
        ) : (
          <PageTitle>
            {getGreeting()}, {user?.firstName || "User"} {user?.lastName}
          </PageTitle>
        )}
      </div>

      {/* Intake CTA for users who haven't completed it */}
      {noIntake && <IntakeCTA />}

      {/* Intake-aware sections */}
      {hasIntake && focus && (
        <IntakeDashboard
          intakeResponse={intakeResponse}
          focus={focus}
          hasEngagements={hasEngagements}
        />
      )}

      <p className="mt-6 mb-2 text-sm font-medium text-gray-500">
        Coaching Dashboard
      </p>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <UserCoaches />

        <UserTasks />

        <UserConversations />
      </div>

      <SummitPromoCard className="mt-6" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Intake CTA — shown to users who haven't completed the intake
// ---------------------------------------------------------------------------

function IntakeCTA() {
  return (
    <div className="mb-6">
      <Link
        to="/intake"
        className="group flex items-center gap-5 rounded-2xl border border-brand-100 bg-brand-50/50 p-6 transition hover:border-brand-200 hover:shadow-md"
      >
        <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-600 transition group-hover:bg-brand-200">
          <Compass className="size-6" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-xl font-medium text-gray-900">
            Discover your path
          </h2>
          <p className="text-text-secondary mt-0.5 text-sm">
            Answer a few questions and get a personalized recommendation for the
            right type of support
          </p>
        </div>
        <ArrowRight className="size-5 shrink-0 text-brand-400 transition group-hover:translate-x-0.5 group-hover:text-brand-600" />
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Intake-aware dashboard sections
// ---------------------------------------------------------------------------

type IntakeResponse = NonNullable<
  ReturnType<typeof useUserIntakeResponse>["data"]
>;

type CurrentFocusDisplay = NonNullable<
  ReturnType<typeof getCurrentFocusDisplay>
>;

function IntakeDashboard({
  intakeResponse,
  focus,
  hasEngagements,
}: {
  intakeResponse: IntakeResponse;
  focus: CurrentFocusDisplay;
  hasEngagements: boolean;
}) {
  const cta = getRecommendedCTA(intakeResponse.recommendedRoute);
  const showLBD = shouldShowLBD(intakeResponse.answers);

  const pressureDomain =
    typeof intakeResponse.answers["pressureDomain"] === "string"
      ? intakeResponse.answers["pressureDomain"]
      : undefined;

  return (
    <div className="mb-6 space-y-4">
      {/* Your Current Focus + Recommended Next Step */}
      <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs font-medium tracking-wide text-gray-400 uppercase">
              Your Current Focus
            </p>
            {focus.keywords.map((kw) => (
              <span
                key={kw}
                className="rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-medium text-brand-700"
              >
                {kw}
              </span>
            ))}
          </div>
          <Link
            to="/intake"
            className="ml-3 flex shrink-0 items-center gap-1.5 text-xs font-medium text-gray-400 transition hover:text-brand-600"
          >
            <RefreshCw className="size-3" />
            Update
          </Link>
        </div>
        <h2 className="mt-2 font-heading text-xl font-medium text-gray-900">
          {focus.headline}
        </h2>

        {intakeResponse.routingSummary && (
          <p className="text-text-secondary mt-3 max-w-prose text-base leading-relaxed">
            {intakeResponse.routingSummary}
          </p>
        )}

        <div className="mt-4">
          <Link
            to="/coach-finder"
            search={
              cta.coachingFormatsPreset
                ? { coachingFormats: cta.coachingFormatsPreset }
                : undefined
            }
            className={buttonVariants({ variant: "primary", size: "sm" })}
          >
            {cta.label}
            <ArrowRight className="ml-2 size-3.5" />
          </Link>
        </div>
      </div>

      {/* Featured coaches — only shown before user has any engagement */}
      {!hasEngagements && (
        <FeaturedCoaches
          pressureDomain={pressureDomain}
          recommendedRoute={intakeResponse.recommendedRoute}
        />
      )}

      {/* Life By Design — expanded banner, conditional */}
      {showLBD && (
        <div className="rounded-2xl border border-brand-100 bg-brand-50/40 p-6">
          <div className="flex items-start gap-4">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-600">
              <Flower className="size-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-base font-medium text-gray-900">
                Life By Design
              </h3>
              <p className="text-text-secondary mt-1 text-sm leading-relaxed">
                Map your vision, set intentional goals, and design the life you
                want.
              </p>
              <Link
                to="/life-design"
                className={cn(buttonVariants({ size: "sm" }), "mt-3")}
              >
                Open Life By Design
                <ArrowRight className="ml-2 size-3.5" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Community recommendation — hidden until communities are live */}
    </div>
  );
}
